/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState } from "react";

// @mui material components
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { styled } from "@mui/system";

const globalCss = require("assets/theme/base/globals");

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Settings page components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";
import DataTable from "Tables/DataTable";
import AdvancedSettlementFilters from "./components/Filters";
import SoftSelect from "components/SoftSelect";

// import BasicInfo from "./components/BasicInfo";
// import ChangePassword from "./components/ChangePassword";
// import DeleteAccount from "./components/DeleteAccount";

// react-router components

import { getData, postData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";
import { useSoftUIController } from "context";

// sweetalert2 components
import Swal from "sweetalert2";

import DialogBox from "Modals/CheckLeavePage";
import { useCallbackPrompt } from "hooks/useCallbackPrompt";

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
    marginLeft: "15px",
  },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="outlined" {...props} />;
});

let settlementOutcomes = [];
let settlements = [];
let originalData = [];
let currentData = [];
let fixtureData = null;

function AdvancedSettlement() {
  const css = globalCss ? globalCss.default : null;
  const customColors = css.customColors;
  const userData = getUserData();
  const [controller, dispatch] = useSoftUIController();
  const { providers } = controller;

  const [updateEnabled, setUpdateEnabled] = useState(false);
  const [filterToggle, setFilterToggle] = useState(true);
  const [applyFilters, setApplyFilters] = useState(false);
  const [displayData, setDisplayData] = useState(false);
  const [selectedFixture, setSelectedFixture] = useState(null);

  const [notification, setNotification] = useState(false);
  const [apiResponse, setApiresponse] = useState("success");
  const [apiResponseMsg, setApiResponseMsg] = useState("success");
  const [modeChanges, setModeChanges] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [provider, setProvider] = useState(null);
  const [providerColor, setProviderColor] = useState("transparent");

  const [showDialog, setShowDialog] = useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(showDialog);

  const [loading, setLoading] = useState(false);

  const [settlementOrigins, setSettlementOrigins] = useState([
    { value: 1, label: "Live Settlement" },
    { value: 3, label: "Operator" },
    { value: 2, label: "Statistics Settlement" },
  ]);

  const [dataTableData, setDataTableData] = useState({
    columns: [
      { Header: "UUID", accessor: "uuid", width: "100px", Cell: formatUuid },
      { Header: "sort", accessor: "sort", width: "100px" },
      // { Header: "Market", accessor: "uni_name", width: "100px", Cell: merge_uniname_numbers },
      { Header: "Market name", accessor: "name", width: "100px" },
      { Header: "Odd e", accessor: "e", width: "100px" },
      { Header: "Odd h", accessor: "h", width: "100px" },
      { Header: "Odd o", accessor: "o", width: "100px" },
      { Header: "Outcome", accessor: "outcome_id", width: "80px", Cell: possibleSettlement },
      { Header: "Conflict", accessor: "origin_id", width: "100px", Cell: formatOrigin },
      { Header: "updated", accessor: "created_at", width: "100px", Cell: formatDate },
    ],

    rows: [],
  });
  const [hiddenColumns, setHiddenColumns] = useState([]);

  const platforms = userData
    ? userData.platforms
      ? userData.platforms.map((platform) => ({
          value: platform.id,
          label: platform.name,
          logo: platform.logo,
          sorting: platform.sorting,
          odds_styles: platform.odds_styles,
        }))
      : []
    : [];

  async function fetchBetsOdds(filters, reset) {
    if (!reset) setDisplayData(false);
    let filtersToSend = { ...filters };
    if (!reset) setFilterToggle(true);
    setLoadingData(true);
    setLoading(true);

    let getOddsEndpoint = `/api/v1/bets/settlements/fixtures`;
    let response = await postData(getOddsEndpoint, filtersToSend, {
      Authorization: getAuthorizationHeader(),
    });
    if (response != null) {
      //console.log(response.data);

      if (response.data.length == 0 || JSON.stringify(response.data.event) == "{}") {
        setApiresponse("error");
        setApiResponseMsg("There are NO Data for the selected filters.");
        setNotification(true);
        setLoadingData(false);
        setLoading(false);
        return;
      }

      let dataTable = { ...dataTableData };
      setDataTableData({ columns: [], rows: [] });
      await sleep(1000);
      setSelectedFixture(response.data.event);
      fixtureData = response.data.event;
      let provider = providers.find((provider) => provider.value == response.data.event.providerId);
      setProvider(provider.label);
      setProviderColor(customColors[provider.label]);
      dataTable.rows = response.data.odds;
      currentData = { ...dataTable };
      originalData = { ...dataTable };

      setHiddenColumns(["updated", "sort"]);
      setDataTableData(dataTable);
      if (dataTable.rows.length > 0 && !reset) setFilterToggle(false);
      if (!reset) setDisplayData(true);
      setLoadingData(false);
      setLoading(false);
    }
  }

  async function fetchSettlementValues() {
    let settlementOutcomesEndPoint = `/api/v1/entities/availableSettlementOutcomes`;
    let response = await getData(settlementOutcomesEndPoint, {
      Authorization: getAuthorizationHeader(),
    });
    if (response != null) {
      //console.log(response.data);
      const noneOption = [{ value: 0, label: "Select..." }];
      const apiOptions = response.data.map((item) => {
        return { value: item.id, label: item.description };
      });

      const allOptions = noneOption.concat(apiOptions);

      settlementOutcomes = allOptions.map((item) => {
        return { value: item.value, label: item.label };
      });
    }
  }

  function linkCell({ value, ...rest }) {
    return (
      <>
        <HtmlTooltip placement="right" title={"Unsettled Odds"}>
          <Button onClick={(e) => handleEditOpenModal(e, rest)}>
            <SoftTypography
              component="span"
              variant="button"
              fontWeight="regular"
              color={"info"}
              opacity={0.8}
              sx={{ lineHeight: 0 }}
            >
              {value}
            </SoftTypography>
          </Button>
        </HtmlTooltip>
      </>
    );
  }

  function formatOrigin({ value, ...rest }) {
    let settler = settlementOrigins.find((item) => item.value == value);
    let conflict = rest.row.original.conflict;
    let currentSettlementId = rest.row.original.settlement_id;
    return (
      <>
        <HtmlTooltip
          placement={"top"}
          maxwidth={500}
          title={
            <React.Fragment>
              {/* <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ display: "block" }}>
                {JSON.stringify(rest.row.original.settlement_history)}
              </div>
            </div> */}
              <ul className="no-bullets">
                {rest.row.original.settlement_history.map((item, index) => {
                  let settlement = settlementOutcomes.find((so) => so.value === item.outcome);
                  let hSettlementId = item.settlement_id;
                  return conflict ? (
                    <li key={item.settlement_id + "-" + index} className="no-bullets">
                      <div style={{ display: "flex" }}>
                        <div
                          style={{ marginRight: "10px" }}
                          className={
                            settlement
                              ? settlement.label.toLowerCase().replace(/ /g, "_") + "-color"
                              : "regular-color"
                          }
                        >
                          {settlement ? settlement.label : "-"}
                        </div>
                        <div style={{ marginRight: "10px" }}>
                          {item.dt ? item.dt.split(".")[0].replace("T", ", ") : "-"}
                        </div>
                        <div>
                          <Icon
                            style={{
                              color: "yellow",
                              display: currentSettlementId == hSettlementId ? "" : "none",
                            }}
                          >
                            star_icon
                          </Icon>
                        </div>
                      </div>
                    </li>
                  ) : (
                    <span style={{ color: "lightGreen" }}>No Conflict on Settlements Exist </span>
                  );
                })}
              </ul>
            </React.Fragment>
          }
        >
          <Icon
            style={{
              color: conflict ? "red" : "green",
              marginLeft: "5px",
            }}
          >
            {conflict ? "close" : "check"}
          </Icon>
        </HtmlTooltip>
      </>
    );
  }

  function formatDate({ value, ...rest }) {
    if (!value) return "-";
    let dateString = value.split(".")[0].replace("T", " ");
    return dateString;
  }

  function formatCompetition({ value, ...rest }) {
    let headerName = rest.column.id;
    return <div className={headerName + "-cell"}>{value}</div>;
  }

  function formatUuid({ value, ...rest }) {
    return (
      <div style={{ cursor: "pointer" }} onClick={(e) => handleUUIDCopy(e, value)}>
        <span className="info-icon">{value.toString().substring(1, 10) + "..."}</span>
        <Icon className="info-icon">copy</Icon>
      </div>
    );
  }

  function possibleSettlement({ value, ...rest }) {
    return (
      <SoftSelect
        className="settlement-outcomes"
        style={{ width: "120px !important" }}
        customSmall={true}
        defaultValue={settlementOutcomes.find((item) => item.value === value)}
        options={settlementOutcomes}
        onChange={(value) => handleSettlementOutcomeChange(value, rest.row)}
      ></SoftSelect>
    );
  }

  function merge_uniname_numbers({ value, ...rest }) {
    var params = rest.row.original.numbers;
    if (params.length == 0) return value;
    var outcome_init = rest.row.original.hasOwnProperty("outcome_id_init")
      ? rest.row.original.outcome_id_init
      : rest.row.original.outcome_id;
    var outcomeInitString = "";
    var indices = [];
    var newtext = value;
    for (var i = 0; i < value.length; i++) {
      if (value[i] === "X" && value[i + 1] === "_") {
        indices.push(i);
        newtext = newtext.replace(value[i], params[indices.length - 1]);
      }
      if (value[i] === "X" && value[i - 1] === "_") {
        indices.push(i);
        newtext = newtext.replace(value[i], params[indices.length - 1]);
      }
      if (value[i] === "A" && value[i + 1] === "_") {
        indices.push(i);
        newtext = newtext.replace(value[i], params[indices.length - 1]);
      }
      if (value[i] === "B" && value[i - 1] === "_") {
        indices.push(i);
        newtext = newtext.replace(value[i], params[indices.length - 1]);
      }
    }
    value = newtext;

    if (outcome_init) {
      outcomeInitString = settlementOutcomes.find((item) => item.value === outcome_init);
      if (outcomeInitString) {
        outcomeInitString = outcomeInitString.label;
      } else {
        outcomeInitString = "No Settled";
      }
    }

    return (
      <HtmlTooltip
        placement={"top"}
        maxwidth={500}
        title={
          <React.Fragment>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <div style={{ display: "block" }}>
                Saved Value in DB: {outcomeInitString ? outcomeInitString : "no settlement"}
              </div>
            </div>
          </React.Fragment>
        }
      >
        <span
          style={{
            color: outcome_init != rest.row.original.outcome_id ? "#cb0c9f" : "",
          }}
        >
          {newtext}
        </span>
      </HtmlTooltip>
    );
  }

  const handleSettlementOutcomeChange = async (item, row) => {
    setUpdateEnabled(false);
    setHiddenColumns([]);
    setDataTableData({ columns: [], rows: [] });
    //console.log("Settlement Change: ", item.value, " for odd: ", row.original.uuid);

    // const data = { ...dataTableData };
    // setDataTableData(data);
    // data.rows = [...currentData];
    // const dataRow = data.rows.find((item) => {
    //   return (item.uuid = row.original.uuid);
    // });
    if (!currentData.rows) return;
    let dataRow = currentData.rows.find((item) => item.uuid == row.original.uuid);
    if (!dataRow.hasOwnProperty("outcome_id_init")) {
      row.original.outcome_id_init = dataRow.outcome_id;
      dataRow.outcome_id_init = dataRow.outcome_id;
    }
    dataRow.updated = "updated";
    row.original.updated = "updated";
    dataRow.outcome_id = item.value;
    setHiddenColumns(["updated", "sort"]);
    setDataTableData(currentData);

    const settlement = {
      uuid: row.original.uuid,
      outcome_id: item.value,
      event_id: fixtureData.eventId,
      provider_id: fixtureData.providerId,
    };
    const settlementExist = settlements.find((item) => item.uuid === settlement.uuid);
    if (!settlementExist) {
      if (settlement.outcome_id === 0) settlement.outcome_id = null;
      settlements.push(settlement);
    } else {
      let settlementExistIndex = settlements.findIndex(
        (item) => item.uuid === settlementExist.uuid
      );
      if (settlement.outcome_id === 0) {
        settlement.outcome_id = null;
      } else {
        settlementExist.outcome_id = settlement.outcome_id;
      }
      if (settlementExist.outcome_id == dataRow.outcome_id_init) {
        settlements.splice(settlementExistIndex, 1);
      }
    }
    if (settlements.length > 0) {
      setUpdateEnabled(true);
      setApiresponse("success");
      setApiResponseMsg("There are unsaved Odd Settlements changes.");
      setNotification(true);
      setShowDialog(true);
    } else {
      setApiresponse("error");
      setApiResponseMsg("There are NO unsaved Odd Settlements changes.");
      setNotification(true);
      setShowDialog(false);
    }
  };

  const handleBetsFilters = (filters, reset) => {
    fetchBetsOdds(filters, reset);
  };

  const handleCancelEdit = async () => {
    if (settlements.length > 0) {
      Swal.fire({
        title: `Unsaved Data!`,
        html: `You are going Lose some Odds you have already settle. </br></br><SoftBox pr={1} style={{ marginRight: "auto" }}>
            <SoftTypography variant="caption">${settlements.length}</SoftTypography>
            <SoftTypography variant="caption"> - </SoftTypography>
            <SoftTypography variant="caption" style="color:#cb0c9f">
              odd Settlements changes
            </SoftTypography>
          </SoftBox></br></br>Are you Sure?`,
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: "Yes, Cancel them!",
        denyButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#87cb16",
        denyButtonColor: "#ff4a55",
      }).then(async (result) => {
        if (result.isConfirmed) {
          //console.log("Modal confirmed");
          setUpdateEnabled(true);
          setUpdateEnabled(false);
          resetData();
        }
      });
    } else {
      //console.log("Nothing have been changed");
    }
  };

  async function resetData() {
    settlements = [];
    if (applyFilters) setApplyFilters(false);
    if (!applyFilters) setApplyFilters(true);
    setShowDialog(false);
  }

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const handleUpdate = () => {
    Swal.fire({
      title: `Unsaved Data!`,
      html: `You are going to Settle a number of Odds. </br></br><SoftBox pr={1} style={{ marginRight: "auto" }}>
            <SoftTypography variant="caption">${settlements.length}</SoftTypography>
            <SoftTypography variant="caption"> - </SoftTypography>
            <SoftTypography variant="caption" style="color:#cb0c9f">
              odd Settlements changes
            </SoftTypography>
          </SoftBox></br></br>Are you Sure?`,
      showDenyButton: true,
      showConfirmButton: true,
      confirmButtonText: "Yes, Settle them!",
      denyButtonText: "Cancel",
      icon: "warning",
      confirmButtonColor: "success",
      // denyButtonColor: "#ff4a55",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const settleOddEndPoint = `/api/v1/bets/settleOdd`;
        const settlementOutcomesVar = getSettlements();
        const response = await postData(
          settleOddEndPoint,
          { signal: "evaluation", values: settlementOutcomesVar },
          {
            Authorization: getAuthorizationHeader(),
          }
        );

        if (response != null) {
          Swal.fire("Request Sent!", `Odds Settlements Sent to system.`, "success").then(
            async (result) => {
              setUpdateEnabled(true);
              setUpdateEnabled(false);
              await sleep(2000);

              resetData();
            }
          );
        } else {
          Swal.fire("Error!", `Something went wrong.`, "error").then(async (result) => {
            setUpdateEnabled(true);
            setUpdateEnabled(false);
            resetData();
          });
        }
      }
    });
  };

  /**
   *
   */
  const handleUUIDCopy = (e, UUID) => {
    //console.log("Copy ApiKey: ", apikey);
    copyToClipboard(UUID);
  };

  const copyToClipboard = (textToCopy) => {
    setApiresponse("success");
    setApiResponseMsg("UUID has been copied to the clipboard!");
    setNotification(true);
    // navigator clipboard api needs a secure context (https)
    if (navigator.clipboard && window.isSecureContext) {
      // navigator clipboard api method'
      return navigator.clipboard.writeText(textToCopy);
    } else {
      // text area method
      let textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      // make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      return new Promise((res, rej) => {
        // here the magic happens
        document.execCommand("copy") ? res() : rej();
        textArea.remove();
      });
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setNotification(false);
  };

  const getSettlements = () => {
    return settlements;
  };

  
  const viewChanges = () => {
    let currentMode = modeChanges;
    if (modeChanges) {
      setModeChanges(false);
    } else {
      setModeChanges(true);
    }
    setHiddenColumns([]);
    setDataTableData({ columns: [], rows: [] });

    if (currentMode) {
      let filteredData = { ...originalData };

      setHiddenColumns(["updated", "sort"]);
      setDataTableData(filteredData);
    } else {
      originalData = { ...currentData };

      let filteredData = { ...currentData };

      filteredData.rows = currentData.rows.filter((item) => {
        return item.updated === "updated";
      });
      setHiddenColumns(["updated", "sort"]);
      setDataTableData(filteredData);
    }
  };

  function searchGoogle(e, verd) {
    e.preventDefault();
    var q = verd;
    window.open("http://google.com/search?q=" + q);
  }

  function searchBetsApi(e, verd) {
    e.preventDefault();
    var q = verd;
    window.open("https://betsapi.com/search/?q=" + q.split(" vs ")[0]);
  }

  useEffect(() => {
    fetchSettlementValues();
  }, []);

  return (
    <DashboardLayout>
      <DialogBox
        // @ts-ignore
        showDialog={showPrompt}
        confirmNavigation={confirmNavigation}
        cancelNavigation={cancelNavigation}
      />
      <DashboardNavbar />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        key={"topright"}
        open={notification}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={`${apiResponse}`}
          sx={{ width: "100%" }}
          className={`api-${apiResponse}-notifications`}
        >
          {apiResponseMsg}
        </Alert>
      </Snackbar>
      <SoftBox my={3} sx={{ marginBottom: "0px" }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={12} sx={{ marginLeft: "0px !important" }}>
            <AdvancedSettlementFilters
              applyFilters={applyFilters}
              settlementOutcomes={settlementOutcomes}
              forceToggle={filterToggle}
              informParent={handleBetsFilters}
              loadingData={loadingData}
            ></AdvancedSettlementFilters>
            <SoftBox style={{ display: displayData ? "" : "none" }}>
              <Grid container spacing={0}>
                <Card style={{ width: "100%" }}>
                  <Grid item xs={12} sx={{ paddingTop: "0px", height: "100%" }}>
                    <SoftBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      pt={2}
                      pl={3}
                      pr={3}
                    >
                      <SoftBox lineHeight={1}>
                        <SoftTypography variant="h5" fontWeight="medium">
                          Fixture&apos;s Odds
                        </SoftTypography>
                        <SoftTypography variant="button" fontWeight="regular" color="text">
                          Here you can find all <span style={{ color: "#cb0c9f" }}>odds</span> of
                          the Selected Ficture
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox lineHeight={1}>
                        <Button disabled={updateEnabled ? false : true} onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                        <Button onClick={viewChanges} disabled={updateEnabled ? false : true}>
                          {modeChanges ? "view All" : "view Changes"}
                        </Button>
                        <Button onClick={handleUpdate} disabled={updateEnabled ? false : true}>
                          Update
                        </Button>
                      </SoftBox>
                    </SoftBox>
                    <SoftBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      pt={2}
                      pl={3}
                      pr={3}
                    >
                      <Grid
                        flexDirection="row"
                        item
                        xs={12}
                        style={{
                          textAlign: "right",
                          paddingRight: "15px",
                          paddingTop: "0px",
                          display: "flex",
                          backgroundColor: "#eee",
                          marginLeft: "auto",
                          marginRight: "auto",
                          paddingLeft: "20px",
                        }}
                      >
                        <SoftBox
                          pr={1}
                          sx={{
                            border: "2px solid " + providerColor,
                            paddingLeft: "10px",
                            backgroundColor: "#fff",
                            boxShadow: "0px 0px 5px 0px #fff !important",
                          }}
                          className={"provider-" + provider + "-competition"}
                        >
                          <SoftTypography
                            variant="text"
                            className={"provider-" + provider + "-competition"}
                          >
                            {selectedFixture ? provider : ""}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          pr={1}
                          style={{
                            fontSize: "1em",
                            paddingTop: "0.4em",
                            color: "#acacac",
                            lineHeight: "1em",
                          }}
                        >
                          <Icon>keyboard_double_arrow_right_icon </Icon>
                        </SoftBox>
                        <SoftBox pr={1}>
                          <SoftTypography variant="text" style={{ color: "#cb0c9f" }}>
                            {selectedFixture ? selectedFixture.sportName : ""}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          pr={1}
                          style={{
                            fontSize: "1em",
                            paddingTop: "0.4em",
                            color: "#acacac",
                            lineHeight: "1em",
                          }}
                        >
                          <Icon>chevron_right_icon </Icon>
                        </SoftBox>
                        <SoftBox pr={1}>
                          <SoftTypography variant="caption">
                            {selectedFixture ? selectedFixture.region : ""}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          pr={1}
                          style={{
                            fontSize: "1em",
                            paddingTop: "0.4em",
                            color: "#acacac",
                            lineHeight: "1em",
                          }}
                        >
                          <Icon>chevron_right_icon </Icon>
                        </SoftBox>
                        <SoftBox pr={1}>
                          <SoftTypography variant="caption" style={{ color: "info" }}>
                            {selectedFixture ? selectedFixture.competition : ""}
                          </SoftTypography>
                        </SoftBox>
                        <div style={{ display: "flex", margin: "auto" }}>
                          <SoftBox pr={1}>
                            <SoftTypography variant="text" style={{ color: "#cb0c9f" }}>
                              {selectedFixture ? selectedFixture.participants[0].name : ""}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox pr={1}>
                            <SoftTypography
                              variant="caption"
                              style={{ color: "rgb(113, 113, 113)", fontSize: "1em" }}
                            >
                              Vs
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox pr={1}>
                            <SoftTypography variant="text" style={{ color: "#cb0c9f" }}>
                              {selectedFixture ? selectedFixture.participants[1].name : ""}
                            </SoftTypography>
                          </SoftBox>
                        </div>

                        <SoftBox>
                          <Button
                            disabled={!selectedFixture}
                            onClick={(e) =>
                              searchGoogle(
                                e,
                                selectedFixture
                                  ? selectedFixture.participants[0].name +
                                      " vs " +
                                      selectedFixture.participants[1].name
                                  : null
                              )
                            }
                          >
                            search web
                          </Button>
                        </SoftBox>
                        <SoftBox>
                          <Button
                            color="warning"
                            disabled={!selectedFixture}
                            onClick={(e) =>
                              searchBetsApi(
                                e,
                                selectedFixture
                                  ? selectedFixture.participants[0].name +
                                      " vs " +
                                      selectedFixture.participants[1].name
                                  : null
                              )
                            }
                          >
                            search betsApi
                          </Button>
                        </SoftBox>
                      </Grid>
                    </SoftBox>
                    <DataTable
                      table={dataTableData}
                      bodyClassName="settlement-fixtures-list"
                      entriesPerPage={{
                        defaultValue: 10,
                        entries: [5, 10, 15, 20, 25],
                      }}
                      hiddenColumns={hiddenColumns}
                      headerClassName="sticky-header"
                      autoResetPageDefault={true}
                      autoResetGlobalFilter={true}
                      autoResetSortBy={true}
                      canSearch
                      openBackDrop={loading}
                    ></DataTable>
                  </Grid>
                </Card>
              </Grid>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default AdvancedSettlement;
